import axios from "axios";
import { extractError, normalizeError } from "../utils/apiErrorUtils";
import { UPLOAD_TIMEOUT_MS } from "../utils/axiosTimeouts";

const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";

// `cfg` is passed to axios as is; `{ quiet: true }` keeps a background reload
// out of the global error snackbar (useAxios.js).
export const getUserCollection = async (userId, cfg) => {
  try {
    const { data } = await axios.get(`${apiUrl}/collection/${userId}`, cfg);
    return data;
  } catch (error) {
    return Promise.reject(extractError(error, "Failed to get collection"));
  }
};

export const getUserCollectionByType = async (userId, type) => {
  try {
    const { data } = await axios.get(`${apiUrl}/collection/${userId}/${type}`);
    return data;
  } catch (error) {
    return Promise.reject(extractError(error, "Failed to get collection"));
  }
};

export const addCardToCollection = async (cardData) => {
  try {
    const { data } = await axios.post(`${apiUrl}/collection`, cardData);
    return data;
  } catch (error) {
    return Promise.reject(extractError(error, "Failed to add card"));
  }
};

export const addBulkToCollection = async (cardIds, collectionType = "glory", condition = "near_mint") => {
  try {
    const { data } = await axios.post(`${apiUrl}/collection/batch`, {
      cardIds,
      collectionType,
      condition,
    });
    return data;
  } catch (error) {
    return Promise.reject(extractError(error, "Failed to add cards"));
  }
};

export const getSetProgress = async () => {
  try {
    const { data } = await axios.get(`${apiUrl}/collection/set-progress`);
    return data;
  } catch (error) {
    return Promise.reject(extractError(error, "Failed to get set progress"));
  }
};

export const updateCollectionCard = async (cardId, updates) => {
  try {
    const { data } = await axios.put(`${apiUrl}/collection/${cardId}`, updates);
    return data;
  } catch (error) {
    return Promise.reject(extractError(error, "Failed to update card"));
  }
};

export const removeCollectionCard = async (cardId) => {
  try {
    const { data } = await axios.delete(`${apiUrl}/collection/${cardId}`);
    return data;
  } catch (error) {
    return Promise.reject(extractError(error, "Failed to remove card"));
  }
};

export const getNearbyListings = async (lng, lat, radius = 50000) => {
  try {
    const { data, headers } = await axios.get(
      `${apiUrl}/collection/nearby-listings?lng=${lng}&lat=${lat}&radius=${radius}`,
      // quietForbidden: the 403 launch-area refusal is shown in place by the
      // map, so it gets no toast on top (E2E audit 15·9c); an outage still
      // does, because not every map state shows it (release review).
      { quietForbidden: true }
    );
    // The feed stays a bare array for old apps, so "there were more than
    // this" comes as a header (B-52).
    const cap = Number(headers?.["x-listings-capped"]);
    if (Array.isArray(data) && cap > 0) data.cappedAt = cap;
    return data;
  } catch (error) {
    // Rejects with { message, status, retryable } rather than a bare string,
    // matching getNearbyUsers and getWishlistMatches (#82). Nearby listings sit
    // behind the SAME discovery limiter as those two, so a throttle here is not
    // exotic — it is what happens once the three surfaces between them spend the
    // shared budget. The Listings tab has to be able to tell that 429 apart from
    // a genuinely empty neighbourhood, and a bare message string cannot (P1-41).
    return Promise.reject(normalizeError(error, "Failed to get nearby listings"));
  }
};

// `signal` [B-63]: the listing sheet aborts the upload when it is closed, so
// a photo the user backed out of is not stored and then saved 20 s later.
export const uploadCardPhotos = async (files, signal) => {
  try {
    const formData = new FormData();
    files.forEach((file) => formData.append("photos", file));
    // [B-68] Card photos must be big enough to show the card; the server
    // checks size and shape only when told the photo is of a card.
    formData.append("purpose", "card");
    const { data } = await axios.post(`${apiUrl}/upload/photos`, formData, {
      signal,
      headers: { "Content-Type": "multipart/form-data" },
      // Overrides the global default (#214). This is the largest request the
      // app makes — five 5 MB photos, then a server-side re-encode and AI
      // moderation pass on each — and aborting one that is nearly complete
      // would be a worse bug than the hang the default exists to stop.
      timeout: UPLOAD_TIMEOUT_MS,
    });
    return data.photos;
  } catch (error) {
    return Promise.reject(extractError(error, "Failed to upload photos"));
  }
};
